import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { AgentApp } from './agent/AgentApp';
import { StudentApp } from './student/StudentApp';
import { api, tokens } from './lib/api';
import { freshStartOnIos } from './lib/freshStart';
import type { Parent } from './lib/types';
import { AdminPortal } from './parent/AdminPortal';
import { AuthPage, ForgotPassword, SetPassword } from './parent/AuthPage';
import { About } from './parent/AboutHelp';
import { ChildPage } from './parent/ChildPage';
import { Dashboard } from './parent/Dashboard';
import { FamilyInviteAccept } from './parent/Family';
import { Landing, marketingPageFor } from './parent/Landing';
import { MfaGate } from './parent/TwoFactor';
import { BillingGate } from './parent/Billing';
import { TermsGate } from './parent/TermsGate';
import { PrivacyPolicy } from './parent/PrivacyPolicy';
import { TermsOfService } from './parent/TermsOfService';
import { AmbassadorTerms } from './parent/AmbassadorTerms';
import { AmbassadorAccount } from './parent/AmbassadorAccount';
import { FeedbackPage, HelpPage } from './parent/HelpFeedback';
import { Wiki } from './parent/wiki/Wiki';
import { Reports } from './parent/Reports';
import { ParentProvider } from './parent/ParentContext';
import { Settings } from './parent/Settings';
import { Shell } from './parent/Shell';
import { Logo } from './ui/Logo';
import { SharePage } from './SharePage';

export function App() {
  const location = useLocation();
  // The child's computer runs its own page and never needs a parent account.
  if (location.pathname.startsWith('/agent')) return <AgentApp />;
  // The Student Portal (#136): a child signs in with a parent-set code + password; no parent account.
  if (location.pathname.startsWith('/student')) return <StudentApp />;
  // A link from an invite or reset email works whoever is signed in here; the new session replaces it.
  if (location.pathname === '/set-password') return <SetPassword onSignedIn={() => window.location.assign('/')} />;
  if (location.pathname === '/family-invite') return <FamilyInviteAccept />;
  // The public privacy policy (#288), for everyone; the browser-extension stores link here.
  if (location.pathname === '/privacy') return <PrivacyPolicy />;
  // The State Compliance page (#702) became the Features guide's State compliance section (#732): old links land there.
  if (location.pathname === '/state-compliance') return <Navigate to="/features#state" replace />;
  // Safety & privacy is part of the Features page's Focused school time card now (#780).
  if (location.pathname === '/safety') return <Navigate to="/features#focus" replace />;
  // The public site's pages besides Home (#702): Features, Safety, FAQ, Brand Ambassadors (#713), Compare plans (#717).
  const marketing = marketingPageFor(location.pathname);
  if (marketing && marketing !== 'home') return <Landing page={marketing} />;
  // Brand Ambassador sign-in (#718): an emailed link to their page, no password.
  if (location.pathname === '/ambassador-sign-in') return <Navigate to="/login" replace />;
  // The Brand Ambassador terms (#715), linked from the application form and each ambassador's page.
  if (location.pathname === '/ambassador-terms') return <AmbassadorTerms />;
  // The public Terms of Service (#567).
  if (location.pathname === '/terms') return <TermsOfService />;
  // A school year a family shared with their evaluator (#612): read-only, no account.
  if (location.pathname.startsWith('/share/')) return <SharePage token={location.pathname.slice('/share/'.length)} />;
  // A partner's own page (#598): their numbers, from a private link, no account.
  // Old private page links (#598) and the email sign-in (#718) are retired (#736): ambassadors sign in like anyone else.
  if (location.pathname.startsWith('/partner/')) return <Navigate to="/login" replace />;
  return <ParentApp />;
}

function ParentApp() {
  const [parent, setParent] = useState<Parent | null>(null);
  const [checking, setChecking] = useState(!!tokens.parent());
  // An admin whose own family is locked for billing (#540) can still open the Admin portal.
  const [adminPastBilling, setAdminPastBilling] = useState(false);

  useEffect(() => {
    if (!tokens.parent()) return;
    api
      .get<Parent>('/api/me')
      .then(setParent)
      .catch((e) => {
        if (e.status === 401) tokens.setParent(null);
      })
      .finally(() => setChecking(false));
  }, []);

  // Signing out (or a session ending) goes to sign-in, not the public home page at /. `navigate` changes on
  // every route change, so keep it in a ref: a changing callback would reconnect the dashboard's socket.
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // Signed in (or finished the required two-factor setup): on iPhone/iPad, load fresh so the bottom bar stays put (#427).
  const signedIn = useCallback((p: Parent) => {
    if (!freshStartOnIos('/')) setParent(p);
  }, []);
  const signedOut = useCallback(() => {
    setParent(null);
    navigateRef.current('/login', { replace: true });
  }, []);

  if (checking) {
    return (
      <main className="center-page">
        <Logo large />
      </main>
    );
  }

  if (!parent) {
    return (
      <Routes>
        {/* The public home page (#261); the rest of the app needs a sign-in. */}
        <Route path="/" element={<Landing />} />
        <Route path="/signup" element={<AuthPage mode="signup" onSignedIn={signedIn} />} />
        <Route path="/login" element={<AuthPage mode="login" onSignedIn={signedIn} />} />
        <Route path="/forgot" element={<ForgotPassword />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // The server requires two-factor and this account hasn't set it up: make them, before anything else.
  if (parent.mfaRequired && !(parent.mfaSatisfied ?? parent.mfaEnabled)) {
    return (
      <ParentProvider key="mfa" initial={parent} onSignOut={signedOut}>
        <MfaGate onDone={signedIn} />
      </ParentProvider>
    );
  }

  // The Terms changed since they accepted, or they never ticked the boxes (#589): accept before anything else.
  if (parent.termsNeeded) {
    return (
      <ParentProvider key="terms" initial={parent} onSignOut={signedOut}>
        <TermsGate onDone={signedIn} />
      </ParentProvider>
    );
  }

  // The family's subscription lapsed (#540): subscribe, fix the card, export data or sign out, before anything else.
  if (parent.billing?.locked && !adminPastBilling) {
    return (
      <ParentProvider key="billing" initial={parent} onSignOut={signedOut}>
        <BillingGate
          onUnlocked={signedIn}
          onAdmin={
            parent.isAdmin
              ? () => {
                  window.history.replaceState(null, '', '/admin');
                  setAdminPastBilling(true);
                }
              : undefined
          }
        />
      </ParentProvider>
    );
  }

  // Each gate and the app get their own provider (#869): it reads `initial` only when it starts, so finishing a gate
  // (choosing a plan, accepting the Terms, setting up two-factor) starts the app with the account as it is now.
  return (
    <ParentProvider key="app" initial={parent} onSignOut={signedOut}>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Dashboard />} />
          <Route path="kids/:id" element={<ChildPage />} />
          <Route path="kids/:id/:tab" element={<ChildPage />} />
          <Route path="settings" element={<Settings />} />
          {/* Family Account merged into Settings (#496): old links land on the same section there. */}
          <Route path="family-account" element={<ToSettings />} />
          {/* Help and Feedback are separate pages (#534): /support lands on Feedback; a request opens at /support/:id. */}
          <Route path="support" element={<Navigate to="/feedback" replace />} />
          <Route path="support/:id" element={<FeedbackPage />} />
          {/* Help & Feedback replaced Feedback (#414). */}
          <Route path="feedback" element={<FeedbackPage />} />
          <Route path="reports" element={<Reports />} />
          <Route path="about" element={<About />} />
          {parent.ambassador && <Route path="ambassador" element={<AmbassadorAccount />} />}
          <Route path="help" element={<HelpPage />} />
          {/* The guide: every option, with search and topics (#536). */}
          <Route path="wiki" element={<Wiki />} />
          {parent.isAdmin && <Route path="admin" element={<AdminPortal />} />}
          {parent.isAdmin && <Route path="admin/:tab" element={<AdminPortal />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </ParentProvider>
  );
}

/** /family-account (before #496) → /settings, keeping the #section so the same card opens. */
function ToSettings() {
  const { hash } = useLocation();
  return <Navigate to={`/settings${hash}`} replace />;
}
